import _ from 'lodash';
import React, { useMemo } from 'react';
import SVGIcon from '../../components/SVGIcon';
import type { ProvisionType } from '../../graphql/generated/graphql';
import { useAllGroupMap } from '../../groups/useAllGroups';
import alertIcon from '../../icons/alert.svg';
import { useAllSlimPostMap } from '../../post/useAllSlimPosts';
import type { EntitySelectConfig } from '../../search/entitySearch/EntitySelect';
import { TopicPolicyAction } from '../../topic/topicPolicyCheck';
import { useAllTopicMap } from '../../topic/useAllTopics';
import populateObjects from '../../utils/populateObjects';
import ProvisionEntitySearchPostPill from './ProvisionEntitySearchPostPill';
import { provisionTopicPolicyCheck } from './provisionAccessCheck';
import styles from './useProvisionEntitySearchConfig.module.scss';

export type ProvisionEntitySearchConfigValues = {
  favoritePostIds: string[];
  favoriteTopicIds: string[];
  groupIds: string[];
  postIds: string[];
  topicIds: string[];
  type: ProvisionType;
};

const useProvisionEntitySearchConfig = (
  values: ProvisionEntitySearchConfigValues,
) => {
  const postMap = useAllSlimPostMap();
  const topicMap = useAllTopicMap();
  const groupMap = useAllGroupMap();

  const provision = useMemo(
    () => ({
      ...values,
      favoritePosts: populateObjects(postMap, values.favoritePostIds),
      favoriteTopics: populateObjects(topicMap, values.favoriteTopicIds),
      groups: populateObjects(groupMap, values.groupIds),
      posts: _.compact(
        values.postIds.map((id) =>
          postMap[id] ? ({ id, __typename: 'Post' } as const) : null,
        ),
      ),
      topics: populateObjects(topicMap, values.topicIds),
    }),
    [postMap, groupMap, topicMap, values],
  );

  return useMemo<{
    config: EntitySelectConfig;
    PostPillComponent: React.ElementType;
  }>(() => {
    const config: EntitySelectConfig = {
      post: {},
      topic: {
        extra: (topic) => {
          if (
            !provisionTopicPolicyCheck(
              TopicPolicyAction.ViewPost,
              topic,
              provision,
            )
          ) {
            return {
              icon: <SVGIcon className={styles.icon}>{alertIcon}</SVGIcon>,
              tooltip:
                'Users will not have access to this topic unless they are added as a member.',
            };
          }

          return {};
        },
      },
    };

    return {
      config,
      PostPillComponent: ProvisionEntitySearchPostPill,
      postPillComponentProps: { provision },
    };
  }, [provision]);
};

export default useProvisionEntitySearchConfig;
